import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from '../../utils/datetime';
import {
  Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StorageIcon from '@mui/icons-material/Storage';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudIcon from '@mui/icons-material/Cloud';
import StreamIcon from '@mui/icons-material/Stream';
import ApiIcon from '@mui/icons-material/Api';
import FileUploadIcon from '@mui/icons-material/FileUpload';

import { listSources, deleteSource, testConnection }
  from '../../redux/actions/ingestion/sources';
import { CMButton } from '../../containers/styledComponents';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import ListingToolbar from '../common/ListingToolbar';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';

// Connector logos
import bigqueryLogo from '../../images/datasources/bigquery.svg';
import kafkaLogo from '../../images/datasources/kafka.svg';
import mysqlLogo from '../../images/datasources/mysql.svg';
import postgreLogo from '../../images/datasources/postgre.svg';
import s3Logo from '../../images/datasources/s3.svg';
import mssqlLogo from '../../images/datasources/mssql.svg';
import webhookLogo from '../../images/datasources/webhook.svg';

const formatDate = (d) => {
  if (!d) return '—';
  try { return format(new Date(d), 'MMM d, yyyy'); } catch (_e) { return '—'; }
};

const safeRelative = (d) => {
  try { if (!d) return null; return formatDistanceToNow(new Date(d), { addSuffix: true }); }
  catch (_e) { return null; }
};

// Pull connectors have last_job_triggered; push webhooks only stamp
// last_event_at. Treat either as "last activity" so a live webhook
// doesn't show Never after deliveries have landed.
const lastActivityAt = (s) => s?.metadata?.last_job_triggered || s?.metadata?.last_event_at || null;
const _lastActivityMs = (s) => {
  const t = lastActivityAt(s);
  return t ? new Date(t).getTime() : 0;
};

const LOGO_MAP = {
  bigquery: bigqueryLogo,
  kafka: kafkaLogo,
  mysql: mysqlLogo,
  postgres: postgreLogo,
  postgresql: postgreLogo,
  s3: s3Logo,
  file: s3Logo,
  mssql: mssqlLogo,
  sqlserver: mssqlLogo,
  webhook: webhookLogo,
};

// Resolve the right glyph for a connector. Logos win when available;
// otherwise fall back to a category icon (DB / Stream / API / File /
// Cloud) so every row shows *something* rather than a blank tile.
const ConnectorGlyph = ({ type, size = 22 }) => {
  const theme = useTheme();
  const lower = String(type || '').toLowerCase();
  const logo = LOGO_MAP[lower];
  if (logo) {
    return (
      <Box
        component="img"
        src={logo}
        alt={lower}
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          borderRadius: 0.5,
          flexShrink: 0,
        }}
      />
    );
  }
  const fallback = (() => {
    switch (lower) {
      case 'kafka': return StreamIcon;
      case 'rest': case 'graphql': case 'webhook': return ApiIcon;
      case 'file': return FileUploadIcon;
      case 'snowflake': case 'redshift': case 'elasticsearch': return CloudIcon;
      case 'postgres': case 'postgresql': case 'mysql':
      case 'sqlserver': case 'mssql': case 'oracle': case 'mongodb':
        return StorageIcon;
      default: return StorageIcon;
    }
  })();
  const Icon = fallback;
  return (
    <Box sx={{
      width: size,
      height: size,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 0.5,
      bgcolor: alpha(theme.palette.text.primary, 0.06),
      color: theme.palette.text.secondary,
      flexShrink: 0,
    }}>
      <Icon sx={{ fontSize: size * 0.7 }} />
    </Box>
  );
};

// Resolve the connector's effective state for the EntityCell stripe.
// "active + tested OK" is the happy path. Non-tested but active is
// neutral (warning that the user hasn't validated). Anything failed
// gets the error tone.
// Friendly one-liner for why a sync got auto-paused (circuit breaker).
const PAUSE_REASON_LABEL = (kind) => {
  switch (kind) {
    case 'signal_types':
    case 'signals':
    case 'plan':   return 'Plan limit reached';
    case 'schema': return 'Schema error';
    case 'config': return 'Config error';
    default:       return 'Auto-paused';
  }
};

const resolveStatus = (s) => {
  const md = s.metadata || {};
  if (s.status !== 'active') return { tone: 'neutral', label: 'Inactive' };
  // A source can be reachable ("Connected") yet have its sync jobs auto-paused
  // by the circuit breaker (plan limit hit, schema error, repeated failures).
  // Surface that prominently so the user knows ingestion has stopped.
  if ((md.paused_jobs || 0) > 0) {
    const allPaused = (md.active_jobs || 0) === 0;
    return {
      tone: 'warning',
      label: allPaused ? 'Paused' : 'Partly paused',
      sublabel: PAUSE_REASON_LABEL(md.pause_kind),
    };
  }
  if (s.test_status === 'success') return { tone: 'success', label: 'Connected' };
  if (s.test_status === 'failed') return { tone: 'error', label: 'Failed' };
  if (s.test_status === 'pending' || s.status === 'testing') return { tone: 'info', label: 'Testing' };
  return { tone: 'warning', label: 'Not Tested' };
};

// Display labels for the connector_type pills in the filter bar.
// Limited to types we have logos / category icons for, plus a few
// common database flavours; the underlying filter is by string match,
// so listing every option isn't required - the user can also filter
// by typing in the search box.
const TYPE_OPTIONS = [
  'postgres', 'mysql', 'mssql', 'oracle', 'mongodb',
  'bigquery', 'snowflake', 'redshift', 'elasticsearch',
  's3', 'file',
  'kafka', 'rest', 'graphql', 'webhook',
];

const STATUS_OPTIONS = [
  { value: 'connected',  label: 'Connected',  color: 'success' },
  { value: 'paused',     label: 'Paused',     color: 'warning' },
  { value: 'failed',     label: 'Failed',     color: 'error'   },
  { value: 'not_tested', label: 'Not Tested', color: 'warning' },
  { value: 'testing',    label: 'Testing',    color: 'info'    },
  { value: 'inactive',   label: 'Inactive',   color: 'default' },
];

// Map a source row to one of the canonical filter status values so
// the chip filter works on the same vocabulary the user sees.
const statusForFilter = (s) => {
  if (s.status !== 'active') return 'inactive';
  if (((s.metadata || {}).paused_jobs || 0) > 0) return 'paused';
  if (s.test_status === 'success') return 'connected';
  if (s.test_status === 'failed') return 'failed';
  if (s.test_status === 'pending' || s.status === 'testing') return 'testing';
  return 'not_tested';
};

const DataSourcesPage = ({
  sources, isLoading, error, isTesting,
  listSources, deleteSource, testConnection,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState(null);

  useEffect(() => { listSources(); }, [listSources]);
  useEffect(() => { setPage(0); }, [search, typeFilter, statusFilter]);

  // Live (no polling): source/job/execution transitions refresh the list so
  // status (Connected/Testing/Paused), job + execution counts and last-run
  // update as ingestion happens.
  useRealtimeRefresh(eventPrefix('ingestion.'), () => listSources());

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (sources || []).filter((s) => {
      if (typeFilter.length > 0 && !typeFilter.includes(String(s.connector_type || '').toLowerCase())) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(statusForFilter(s))) return false;
      if (!q) return true;
      return (s.name || '').toLowerCase().includes(q)
        || (s.connector_type || '').toLowerCase().includes(q)
        || (s.source_id || '').toLowerCase().includes(q);
    });
  }, [sources, search, typeFilter, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDirection === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av; let bv;
      switch (sortColumn) {
        case 'name': av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); break;
        case 'type': av = (a.connector_type || ''); bv = (b.connector_type || ''); break;
        case 'jobs': av = (a.metadata?.total_jobs || 0); bv = (b.metadata?.total_jobs || 0); break;
        case 'executions':
          av = (a.metadata?.total_executions || 0); bv = (b.metadata?.total_executions || 0); break;
        case 'last_run':
          av = _lastActivityMs(a);
          bv = _lastActivityMs(b);
          break;
        case 'created_at':
        default:
          av = a.created_at ? new Date(a.created_at).getTime() : 0;
          bv = b.created_at ? new Date(b.created_at).getTime() : 0;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortColumn, sortDirection]);

  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage],
  );

  const handleCreateNew = () => navigate('/datasources/connect');
  const handleConnectorClick = (sourceId) => navigate(`/datasources/${sourceId}`);
  const handleEditSource = (sourceId) => navigate(`/datasources/edit/${sourceId}`);
  const handleTestConnection = (sourceId) => testConnection(sourceId);
  const handleDeleteSource = (source) => {
    setSourceToDelete(source);
    setDeleteDialogOpen(true);
  };
  const confirmDelete = () => {
    if (sourceToDelete) {
      deleteSource(sourceToDelete.source_id);
      setDeleteDialogOpen(false);
      setSourceToDelete(null);
    }
  };
  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setSourceToDelete(null);
  };

  const toggleType = (t) =>
    setTypeFilter((prev) => prev.includes(t) ? prev.filter((v) => v !== t) : [...prev, t]);
  const toggleStatus = (s) =>
    setStatusFilter((prev) => prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s]);

  const activeFilterCount =
    (search ? 1 : 0)
    + (typeFilter.length > 0 ? 1 : 0)
    + (statusFilter.length > 0 ? 1 : 0);

  const clearAll = () => {
    setSearch('');
    setTypeFilter([]);
    setStatusFilter([]);
  };

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <PageBreadcrumbs items={[{ text: 'Data Sources', icon: <StorageIcon /> }]} sx={{ mb: 2 }} />
        <Box sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main, borderRadius: 1 }}>
          <Typography variant="h6">Error Loading Data Sources</Typography>
          <Typography variant="body2">{error.message || String(error)}</Typography>
        </Box>
      </Box>
    );
  }

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (s) => {
        const r = resolveStatus(s);
        const cell = <EntityCell id={s.source_id} tone={r.tone} label={r.label} sublabel={r.sublabel} />;
        const reason = (s.metadata || {}).pause_reason;
        // On a paused source, the full auto-pause reason is worth a hover.
        return reason && r.label.toLowerCase().includes('paus')
          ? <Tooltip title={reason} placement="right" arrow>{cell}</Tooltip>
          : cell;
      },
    },
    {
      key: 'name',
      label: 'Source',
      sortable: true,
      width: '32%',
      render: (s) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ConnectorGlyph type={s.connector_type} size={26} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{
              fontWeight: 600, fontSize: 13, lineHeight: 1.3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 320,
            }}>
              {s.name || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{
              fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
            }}>
              {s.connector_type || ''}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: 'jobs',
      label: 'Jobs',
      sortable: true,
      width: 80,
      align: 'right',
      render: (s) => {
        const v = s.metadata?.total_jobs || 0;
        return (
          <Typography variant="body2" sx={{
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: v > 0 ? 600 : 400,
            color: v > 0 ? theme.palette.text.primary : theme.palette.text.disabled,
          }}>
            {v.toLocaleString()}
          </Typography>
        );
      },
    },
    {
      key: 'executions',
      label: 'Executions',
      sortable: true,
      width: 110,
      align: 'right',
      render: (s) => {
        const v = s.metadata?.total_executions || 0;
        return (
          <Typography variant="body2" sx={{
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            fontWeight: v > 0 ? 600 : 400,
            color: v > 0 ? theme.palette.text.primary : theme.palette.text.disabled,
          }}>
            {v.toLocaleString()}
          </Typography>
        );
      },
    },
    {
      key: 'last_run',
      label: 'Last Run',
      sortable: true,
      width: 140,
      render: (s) => {
        const last = lastActivityAt(s);
        if (!last) {
          return <Typography variant="body2" color="text.disabled" sx={{ fontSize: 13 }}>Never</Typography>;
        }
        return (
          <Tooltip title={formatDate(last)} arrow placement="top">
            <Box>
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                {safeRelative(last)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {formatDate(last)}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 120,
      render: (s) => (
        <Tooltip title={formatDate(s.created_at)} arrow placement="top">
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {formatDate(s.created_at)}
            </Typography>
            {s.created_at && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {safeRelative(s.created_at)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 80,
      align: 'right',
      stopRowClick: true,
      render: (s) => (
        <RowActions
          onOpen={() => handleConnectorClick(s.source_id)}
          openLabel="Open data source"
          items={[
            {
              label: 'Test connection',
              icon: PlayArrowIcon,
              disabled: isTesting,
              onClick: () => handleTestConnection(s.source_id),
            },
            {
              label: 'Edit',
              icon: EditIcon,
              onClick: () => handleEditSource(s.source_id),
            },
            { divider: true },
            {
              label: 'Delete',
              icon: DeleteOutlineIcon,
              danger: true,
              onClick: () => handleDeleteSource(s),
            },
          ]}
        />
      ),
    },
  ];

  const filtersNode = (
    <>
      <TextField
        size="small"
        placeholder="Search data sources by name or type…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 260, flex: 1 }}
      />
      <Autocomplete
        multiple
        size="small"
        options={TYPE_OPTIONS}
        value={typeFilter}
        onChange={(_, val) => setTypeFilter(val)}
        getOptionLabel={(o) => o}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ConnectorGlyph type={option} size={18} />
            <Typography variant="body2" sx={{ textTransform: 'capitalize', fontSize: 13 }}>
              {option}
            </Typography>
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Connector type" placeholder={typeFilter.length ? '' : 'Any'} />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={option}
              size="small"
            />
          ))
        }
        sx={{ minWidth: 240, flex: 1 }}
        limitTags={2}
      />
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Status
        </Typography>
        {STATUS_OPTIONS.map((opt) => {
          const selected = statusFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleStatus(opt.value)}
              color={selected ? opt.color : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
    </>
  );

  const actionsNode = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        size="small"
        variant="outlined"
        startIcon={<FileUploadIcon sx={{ fontSize: 16 }} />}
        onClick={() => navigate('/datasources/uploads')}
        sx={{ textTransform: 'none' }}
      >
        Past uploads
      </Button>
      <CMButton startIcon={<AddIcon />} onClick={handleCreateNew} size="small">
        New Data Source
      </CMButton>
    </Stack>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs items={[{ text: 'Data Sources', icon: <StorageIcon /> }]} mb={0.5} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        Connectors that ingest raw data into your workspace. Each source can run multiple ingestion jobs on a schedule.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        actions={actionsNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
        trailing={activeFilterCount > 0 ? `${sorted.length} of ${(sources || []).length}` : null}
      />

      <DataTable
        columns={columns}
        rows={paged}
        getRowKey={(row) => row.source_id}
        onRowClick={(row) => handleConnectorClick(row.source_id)}
        loading={isLoading && (!sources || sources.length === 0)}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={(key, dir) => { setSortColumn(key); setSortDirection(dir); }}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={sorted.length}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        emptyState={{
          icon: <StorageIcon sx={{ fontSize: 32 }} />,
          title: activeFilterCount > 0 ? 'No data sources match your filters' : 'No Data Sources Yet',
          message: activeFilterCount > 0
            ? 'Try a different keyword or clear the connector / status filters.'
            : 'Connect your first data source to start ingesting data into the platform.',
          action: activeFilterCount > 0 ? null : (
            <CMButton startIcon={<AddIcon />} onClick={handleCreateNew}>
              Add Your First Data Source
            </CMButton>
          ),
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Delete Data Source</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{sourceToDelete?.name}"?
            {sourceToDelete?.metadata?.total_jobs > 0 && (
              <Box component="span" sx={{ display: 'block', mt: 1, color: theme.palette.error.main }}>
                This will also delete {sourceToDelete.metadata.total_jobs} associated import job
                {sourceToDelete.metadata.total_jobs === 1 ? '' : 's'}.
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  sources: state.ingestion_sources?.sources || [],
  isLoading: state.ingestion_sources?.isLoading || false,
  error: state.ingestion_sources?.error,
  testResults: state.ingestion_sources?.testResults || {},
  isTesting: state.ingestion_sources?.isTesting || false,
});

const mapDispatchToProps = {
  listSources,
  deleteSource,
  testConnection,
};

export default connect(mapStateToProps, mapDispatchToProps)(DataSourcesPage);
